import * as connectionActions from '../../state/modules/connection/actions';
import store from '../../state';

export const checkMemory = targetPercentage => {
  // performance.memory is available in Chromium browsers
  if (globalThis.performance && globalThis.performance.memory) {
    const { usedJSHeapSize, jsHeapSizeLimit } = globalThis.performance.memory;

    if (usedJSHeapSize === undefined || jsHeapSizeLimit === undefined) {
      console.warn('[Memory Monitor] performance.memory is not available in this browser.');
      return;
    }

    if (jsHeapSizeLimit === 0) {
      console.warn(
        '[Memory Monitor] jsHeapSizeLimit is 0. This may indicate that the browser does not support memory monitoring.'
      );
      return;
    }

    // Convert bytes to Megabytes
    const usedMB = Math.round(usedJSHeapSize / (1024 * 1024));
    const limitMB = Math.round(jsHeapSizeLimit / (1024 * 1024));

    // Calculate current percentage used (e.g., 0.45 = 45%)
    const currentUsageRatio = usedJSHeapSize / jsHeapSizeLimit;

    console.deviceLogDebug(
      `[Memory Monitor] Used: ${usedMB} MB / Limit: ${limitMB} MB (${Math.round(currentUsageRatio * 100)}%)`
    );

    // Trigger reload if used memory exceeds our percentage threshold
    if (currentUsageRatio >= targetPercentage) {
      console.deviceLogInfo(
        `[Memory Monitor] Reached ${Math.round(targetPercentage * 100)}% of heap limit! In the future, reloading...`
      );
      globalThis.safePageReload();
    }
  }
};

// See MonitoringChannel in app/channels/monitoring_channel.rb for the
// server-side implementation of this channel. It is used to monitor the
// connection status of the ActionCable connection, and to trigger a ping-pong
// mechanism to keep the connection alive and detect disconnections.
const startActionCableMonitor = actionCable => {
  if (!actionCable) {
    console.log('ActionCable consumer not initialized. Cannot start monitor.');
    return;
  }

  const pingInterval = 10_000; // 10 seconds
  const pingEnabled = true; // Set to true to enable ping-pong mechanism for connection monitoring

  actionCable.subscriptions.create('MonitoringChannel', {
    initialized() {
      this.ping = this.ping.bind(this);
      this.lastPongAt = Date.now();
      this.handlingReload = false;
      this.pingScheduled = false;
    },
    connected() {
      console.deviceLogInfo('[Real-time API] Connection established');
      store.dispatch(connectionActions.connected());
      return this.schedulePing();
    },
    disconnected() {
      // Note that the disconnected callback is not always called when the
      // connection is lost, so we also rely on the ping-pong mechanism to
      // detect disconnections.
      console.deviceLogInfo('[Real-time API] Connection lost (or navigating away)');
      // When disconnected, we schedule a ping immediately. If the connection does not
      // recover within the minute, a refresh will ensue.
      this.schedulePing();
      return store.dispatch(connectionActions.disconnected());
    },
    received(data) {
      switch (data.action) {
        case 'pong': {
          store.dispatch(connectionActions.connected());
          return this.pong();
        }
        case 'pubsub_pong': {
          store.dispatch(connectionActions.connected());
          return this.pong();
        }
      }
    },
    ping() {
      this.perform('ping');
      return this.schedulePing();
    },
    pong() {
      this.lastPongAt = Date.now();
      this.missingPongsLogged = false;
      return this.schedulePing();
    },
    schedulePing() {
      if (!pingEnabled) return;

      const lastPongAgo = Date.now() - this.lastPongAt;

      // Make sure that there's only ever one call reload the page. Under certain conditions
      // If multiple calls are made at the same time, several reloads will be requested.
      // I a worst case scenario, this leads to API quota limits.
      if (!this.handlingReload && lastPongAgo > pingInterval * 6) {
        this.handlingReload = true;
        const lastPongSecondsAgo = Math.round(lastPongAgo / 1000);
        console.deviceLogWarning(
          `[Real time API]: No pong received for ${lastPongSecondsAgo} seconds. Connection might be lost. Reloading page...`
        );
        globalThis.safePageReload();
      }

      if (!this.pingScheduled) {
        clearTimeout(this.scheduledPing);
        this.scheduledPing = setTimeout(() => {
          this.pingScheduled = false;
          this.ping();
        }, pingInterval);
      }

      this.pingScheduled = true;
    }
  });
};

export { startActionCableMonitor };
